import { alpha, Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MODULE_ACCENT_COLORS } from '../../../theme/module-colors';
import type { WizardStep } from '../hooks/use-wizard-draft';

const accent = MODULE_ACCENT_COLORS.goodsReceiving.main;
const STEP_ORDER: WizardStep[] = [
  'supplier',
  'document',
  'items',
  'checklist',
  'storage',
  'summary',
];

interface WizardStepHeaderProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  /** Main wizard step — drives the "Passo N di 6" indicator. Omit for sub-steps. */
  step?: WizardStep;
}

export function WizardStepHeader({ icon, title, subtitle, step }: WizardStepHeaderProps) {
  const { t } = useTranslation('goodsReceiving');
  const index = step ? STEP_ORDER.indexOf(step) : -1;
  const position =
    index >= 0 ? t('stepProgress', { current: index + 1, total: STEP_ORDER.length }) : null;

  return (
    <Stack direction="row" spacing={1.75} sx={{ alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.12),
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {position && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: alpha(accent, 0.95), fontWeight: 600 }}
          >
            {position}
          </Typography>
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
